import { useRef, useState } from 'react';
import { Button } from '@/components/ui';
import { mediaUrl, uploadImage, deleteImage, UploadError } from '@/lib/media';

/**
 * Profile photo and gallery upload.
 *
 * project.md §7 asks for a profile picture and additional pictures; §26 asks
 * for the same on the companion application. On a marketplace where strangers
 * meet, a real photograph is the single largest trust signal — a listing
 * without one converts far worse than one with.
 *
 * Uploads go straight from the browser to Supabase Storage. The write is
 * guarded by a storage policy requiring the path to start with the uploader's
 * own user id, so this never needs the service role and never touches our API.
 */
export function AvatarUpload({
  userId,
  value,
  onChange,
  disabled,
}: {
  userId: string;
  value: string | null;
  onChange: (key: string | null) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const previous = value;
      onChange(await uploadImage(userId, file));

      // Only after the new one is safely stored. Deleting first would lose the
      // old photo if the upload failed.
      if (previous) void deleteImage(previous);
    } catch (err) {
      setError(err instanceof UploadError ? err.message : 'Upload failed. Try again.');
    }
    setBusy(false);
    if (input.current) input.current.value = '';
  }

  const src = mediaUrl(value);

  return (
    <div>
      <div className="flex items-center gap-4">
        <div
          className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-full"
          style={{ background: 'var(--surface-2)' }}
        >
          {src ? (
            <img src={src} alt="Your profile" className="size-full object-cover" />
          ) : (
            <svg viewBox="0 0 24 24" className="size-8" fill="none" aria-hidden>
              <path
                d="M12 12a4 4 0 100-8 4 4 0 000 8zM4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1"
                stroke="var(--muted)"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy || disabled}
              onClick={() => input.current?.click()}
            >
              {busy ? 'Uploading…' : src ? 'Change photo' : 'Add photo'}
            </Button>
            {src && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy || disabled}
                onClick={() => {
                  if (value) void deleteImage(value);
                  onChange(null);
                }}
              >
                Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-[var(--muted)]">
            A clear photo of your face. Resized automatically — any size is fine.
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-2 text-xs" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void pick(e.target.files?.[0])}
      />
    </div>
  );
}

/** Up to six extra pictures — the cap the CHECK constraint in 0013 enforces. */
const MAX_GALLERY = 6;

export function GalleryUpload({
  userId,
  value,
  onChange,
  disabled,
}: {
  userId: string;
  value: string[];
  onChange: (keys: string[]) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const room = MAX_GALLERY - value.length;

  async function add(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);

    const chosen = Array.from(files).slice(0, room);
    const uploaded: string[] = [];

    for (const file of chosen) {
      try {
        uploaded.push(await uploadImage(userId, file));
      } catch (err) {
        // Keep whatever succeeded. Discarding four good uploads because the
        // fifth was a HEIC the browser could not decode would be worse.
        setError(err instanceof UploadError ? err.message : 'Some images failed to upload.');
      }
    }

    if (uploaded.length > 0) onChange([...value, ...uploaded]);
    setBusy(false);
    if (input.current) input.current.value = '';
  }

  function remove(key: string) {
    onChange(value.filter((k) => k !== key));
    void deleteImage(key);
  }

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {value.map((key) => (
          <div key={key} className="group relative size-24 overflow-hidden rounded-xl">
            <img
              src={mediaUrl(key)}
              alt=""
              className="size-full object-cover"
              style={{ background: 'var(--surface-2)' }}
            />
            <button
              type="button"
              disabled={disabled}
              onClick={() => remove(key)}
              aria-label="Remove this picture"
              className="absolute top-1 right-1 grid size-6 place-items-center rounded-full text-xs opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              style={{ background: 'rgb(0 0 0 / 0.6)', color: '#fff' }}
            >
              ✕
            </button>
          </div>
        ))}

        {room > 0 && (
          <button
            type="button"
            disabled={busy || disabled}
            onClick={() => input.current?.click()}
            className="grid size-24 place-items-center rounded-xl border border-dashed text-sm transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"
            style={{ borderColor: 'var(--line)', color: 'var(--ink-2)' }}
          >
            {busy ? '…' : '+ Add'}
          </button>
        )}
      </div>

      <p className="mt-2 text-xs text-[var(--muted)]">
        {room > 0 ? `${room} more allowed.` : 'Maximum reached.'} Shown on your public profile.
      </p>

      {error && (
        <p className="mt-1 text-xs" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => void add(e.target.files)}
      />
    </div>
  );
}
